import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { BookOpen, Clock, Eye, ArrowLeft, Search, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  post_op_recovery:      'Post-op recovery',
  medication_adherence:  'Medication adherence',
  fall_prevention:       'Fall prevention',
  wound_care:            'Wound care',
  nutrition:             'Nutrition',
  cognitive_health:      'Cognitive health',
  caregiver_burnout:     'Caregiver burnout',
  chronic_conditions:    'Chronic conditions',
  advance_care_planning: 'Advance care planning',
  telehealth_prep:       'Telehealth prep',
};

/** /patient/education + /patient/education/:slug — Library (Blueprint #33). */
export default function PatientEducation() {
  const { slug } = useParams();
  if (slug) return <ArticleDetail slug={slug} />;
  return <LibraryIndex />;
}

function LibraryIndex() {
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [activeCat, setActiveCat] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [recs, setRecs] = useState(null);
  const { user, getAuthHeader } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeCat !== 'all') params.set('category', activeCat);
      if (q) params.set('q', q);
      const { data } = await axios.get(`${API}/patient/education?${params.toString()}`);
      setList(data.articles);
      setCats(data.categories);
    } catch (e) {
      toast.error('Could not load education library');
    } finally {
      setLoading(false);
    }
  }, [activeCat, q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // Load personalized recs on first paint (patient-only).
  useEffect(() => {
    if (user?.role !== 'patient') return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/patient/education/recommended/${user.id}`, getAuthHeader());
        setRecs(data);
      } catch (e) { /* non-fatal */ }
    })();
  }, [user, getAuthHeader]);

  return (
    <DashboardShell data-testid="education-library-page">
      <header className="mb-6">
        <div className="cn-eyebrow inline-flex items-center gap-1.5">
          <BookOpen className="w-3 h-3" /> Care · Education
        </div>
        <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Patient Education Library</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Evidence-based guides curated for your care plan — read in 5 minutes, applicable immediately.
        </p>
      </header>

      {/* Personalized recommendations strip */}
      {recs && recs.articles?.length > 0 && (
        <section className="rounded-2xl bg-cn-primary/5 border border-cn-primary/20 p-5 mb-6" data-testid="education-recs">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-cn-primary mb-3">
            <Sparkles className="w-3 h-3" /> Recommended for you
            {recs.strategy === 'diagnosis_anchored' && <span className="text-cn-text-muted">· based on your care plan</span>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {recs.articles.slice(0, 3).map((a) => (
              <Link key={a.slug} to={`/patient/education/${a.slug}`} className="rounded-xl bg-white p-4 hover:shadow-md transition border border-slate-100" data-testid={`rec-${a.slug}`}>
                <div className="text-[10px] uppercase tracking-widest text-cn-primary mb-1">{CATEGORY_LABELS[a.category] || a.category}</div>
                <div className="font-semibold text-slate-900 line-clamp-2">{a.title}</div>
                <div className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {a.read_minutes} min
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4 items-center" data-testid="education-filters">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search articles…"
            className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-full focus:outline-none focus:border-cn-primary"
            data-testid="education-search"
          />
        </div>
        <button
          onClick={() => setActiveCat('all')}
          className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition ${activeCat === 'all' ? 'bg-cn-primary text-white border-cn-primary' : 'bg-white border-slate-200 hover:border-cn-primary/50'}`}
          data-testid="education-cat-all"
        >All</button>
        {cats.map((c) => (
          <button
            key={c}
            onClick={() => setActiveCat(c)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition whitespace-nowrap ${activeCat === c ? 'bg-cn-primary text-white border-cn-primary' : 'bg-white border-slate-200 hover:border-cn-primary/50'}`}
            data-testid={`education-cat-${c}`}
          >
            {CATEGORY_LABELS[c] || c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="rounded-2xl bg-white border border-slate-100 p-8 text-slate-500">Loading articles…</div>
      ) : list.length === 0 ? (
        <div className="rounded-2xl bg-white border border-slate-100 p-12 text-center" data-testid="education-empty">
          <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-700 font-semibold">No articles matched.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="education-grid">
          {list.map((a) => (
            <Link key={a.slug} to={`/patient/education/${a.slug}`} className="rounded-2xl bg-white border border-slate-100 p-5 hover:shadow-md hover:border-cn-primary/30 transition group" data-testid={`education-card-${a.slug}`}>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-cn-primary mb-2">
                {CATEGORY_LABELS[a.category] || a.category}
              </div>
              <h3 className="font-bold text-slate-900 group-hover:text-cn-primary transition mb-2 line-clamp-2">{a.title}</h3>
              <p className="text-sm text-slate-600 line-clamp-3">{a.summary}</p>
              <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {a.read_minutes} min</span>
                <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {a.views || 0}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}

function ArticleDetail({ slug }) {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getAuthHeader, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/patient/education/${slug}`);
        setArticle(data);
        if (isAuthenticated) {
          // Track view — fire-and-forget.
          axios.post(`${API}/patient/education/${slug}/track-view`, {}, getAuthHeader()).catch(() => {});
        }
      } catch (e) {
        toast.error('Article not found');
        navigate('/patient/education', { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, isAuthenticated, getAuthHeader, navigate]);

  if (loading) {
    return (
      <DashboardShell data-testid="education-article-loading">
        <div className="rounded-2xl bg-white border border-slate-100 p-8 text-slate-500">Loading article…</div>
      </DashboardShell>
    );
  }
  if (!article) return null;

  return (
    <DashboardShell data-testid="education-article-page">
      <Button variant="ghost" onClick={() => navigate('/patient/education')} className="mb-4 -ml-2" data-testid="article-back">
        <ArrowLeft className="w-4 h-4 mr-2" /> Library
      </Button>
      <article className="max-w-3xl">
        <div className="text-[10px] uppercase tracking-widest text-cn-primary mb-2">{CATEGORY_LABELS[article.category] || article.category}</div>
        <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight">{article.title}</h1>
        <div className="flex items-center gap-3 mt-3 text-sm text-slate-500">
          <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4" /> {article.read_minutes} min read</span>
          <span className="inline-flex items-center gap-1"><Eye className="w-4 h-4" /> {article.views || 0} reads</span>
        </div>
        <p className="text-lg text-slate-700 mt-6 leading-relaxed">{article.summary}</p>
        <div className="prose prose-slate max-w-none mt-6 text-slate-800 leading-relaxed whitespace-pre-wrap" data-testid="article-body">
          {article.body}
        </div>
        {article.tags?.length > 0 && (
          <div className="mt-8 pt-6 border-t border-slate-100 flex flex-wrap gap-2">
            {article.tags.map((t) => (
              <span key={t} className="px-2.5 py-1 text-xs bg-slate-100 text-slate-700 rounded-full">#{t}</span>
            ))}
          </div>
        )}
      </article>
    </DashboardShell>
  );
}
